import { RagBadge } from "./RagBadge.js";
import {
  hasAnyDocumentActions,
  RAG_DOCUMENT_STATUS_LABELS,
  RagDocumentActions,
} from "./RagDocumentActions.js";
import type { DocumentStatus } from "../types.js";

interface RagDocumentsTableProps {
  documents: {
    id: string;
    title: string;
    type: string;
    size: string;
    updatedAt: string;
    versionLabel?: string;
    isResynced?: boolean;
    status: DocumentStatus;
  }[];
  onView?: (documentId: string) => void;
  onReprocess?: (documentId: string) => void;
  onDelete?: (documentId: string) => void;
  onStatus?: (documentId: string) => void;
}

export function RagDocumentsTable({
  documents,
  onView,
  onReprocess,
  onDelete,
  onStatus,
}: RagDocumentsTableProps) {
  const showActionsColumn = hasAnyDocumentActions({ onView, onReprocess, onDelete, onStatus });

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--lm-border-subtle)] bg-white shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-transparent dark:shadow-none">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="bg-[var(--surface-sunken)] text-left text-[11px] font-medium uppercase text-[#576274] dark:bg-slate-900/60 dark:text-slate-400">
            <th className="px-4 py-2.5">Title</th>
            <th className="px-4 py-2.5">Type</th>
            <th className="px-4 py-2.5">Size</th>
            <th className="px-4 py-2.5">Updated</th>
            <th className="px-4 py-2.5">Status</th>
            {showActionsColumn ? <th className="px-4 py-2.5">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr
              key={doc.id}
              className="border-t border-[rgba(15,23,42,0.09)] hover:bg-[var(--lm-row-hover)] dark:border-white/10 dark:hover:bg-white/[0.02]"
            >
              <td className="px-4 py-3 text-[#101828] dark:text-white">
                <div className="flex flex-wrap items-center gap-2">
                  <span>{doc.title}</span>
                  {doc.versionLabel ? (
                    <span className="rounded bg-slate-700/80 px-1.5 py-0.5 font-mono text-[10px] text-slate-300">
                      v {doc.versionLabel}
                    </span>
                  ) : null}
                </div>
              </td>
              <td className="px-4 py-3 text-slate-300">{doc.type}</td>
              <td className="px-4 py-3 text-slate-300">{doc.size}</td>
              <td className="px-4 py-3 text-slate-300">{doc.updatedAt}</td>
              <td className="px-4 py-3">
                <RagBadge variant={doc.status}>{RAG_DOCUMENT_STATUS_LABELS[doc.status]}</RagBadge>
              </td>
              {showActionsColumn ? (
                <td className="px-4 py-3">
                  <RagDocumentActions
                    status={doc.status}
                    documentId={doc.id}
                    onView={onView}
                    onReprocess={onReprocess}
                    onDelete={onDelete}
                    onStatus={onStatus}
                  />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
